import { getFontSized } from "@tamagui/get-font-sized";
import { getSpace } from "@tamagui/get-token";
import { User } from "@tamagui/lucide-icons-2";
import { useState } from "react";
import { Label, Button as TButton, Input as TInput, Text, View, XGroup, createStyledContext, getFontSize, getVariable, isWeb, styled, useGetThemedIcon, useTheme, withStaticProperties } from "tamagui";
import { jsx, jsxs } from "react/jsx-runtime";
const defaultContextValues = {
  size: "$true",
  scaleIcon: 1.2,
  color: void 0
};
const InputContext = createStyledContext(defaultContextValues);
const defaultInputGroupStyles = {
  size: "$true",
  fontFamily: "$body",
  borderWidth: 1,
  outlineWidth: 0,
  color: "$color",
  ...(isWeb ? {
    tabIndex: 0
  } : {
    focusable: true
  }),
  borderColor: "$borderColor",
  backgroundColor: "$color2",
  // this fixes a flex bug where it overflows container
  minWidth: 0,
  hoverStyle: {
    borderColor: "$borderColorHover"
  },
  focusStyle: {
    outlineColor: "$outlineColor",
    outlineWidth: 2,
    outlineStyle: "solid",
    borderColor: "$borderColorFocus"
  }
};
const InputGroupFrame = styled(XGroup, {
  justifyContent: "space-between",
  context: InputContext,
  variants: {
    unstyled: {
      false: defaultInputGroupStyles
    },
    scaleIcon: {
      ":number": {}
    },
    applyFocusStyle: {
      ":boolean": (val, {
        props
      }) => {
        if (val) {
          return props.focusStyle || defaultInputGroupStyles.focusStyle;
        }
      }
    },
    size: {
      "...size": (val, {
        tokens
      }) => {
        return {
          borderRadius: tokens.radius[val]
        };
      }
    }
  },
  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === "1" ? true : false
  }
});
const FocusContext = createStyledContext({
  setFocused: val => {},
  focused: false
});
const InputGroupImpl = InputGroupFrame.styleable((props, forwardedRef) => {
  const {
    children,
    ...rest
  } = props;
  const [focused, setFocused] = useState(false);
  return /* @__PURE__ */jsx(FocusContext.Provider, {
    focused,
    setFocused,
    children: /* @__PURE__ */jsx(InputGroupFrame, {
      applyFocusStyle: focused,
      ref: forwardedRef,
      ...rest,
      children
    })
  });
});
const inputSizeVariant = (val = "$true", extras) => {
  const radiusToken = extras.tokens.radius[val] ?? extras.tokens.radius["$true"];
  const paddingHorizontal = getSpace(val, {
    shift: -1,
    bounds: [2]
  });
  const fontStyle = getFontSized(val, extras);
  if (!isWeb && fontStyle) {
    delete fontStyle["lineHeight"];
  }
  return {
    ...fontStyle,
    height: val,
    borderRadius: extras.props.circular ? 1e5 : radiusToken,
    paddingHorizontal
  };
};
const InputFrame = styled(TInput, {
  unstyled: true,
  context: InputContext
});
const InputImpl = InputFrame.styleable((props, ref) => {
  const {
    setFocused
  } = FocusContext.useStyledContext();
  const {
    size
  } = InputContext.useStyledContext();
  const {
    onChange,
    ...rest
  } = props;
  return /* @__PURE__ */jsx(View, {
    flex: 1,
    children: /* @__PURE__ */jsx(InputFrame, {
      ref,
      onFocus: () => {
        setFocused(true);
      },
      onBlur: () => setFocused(false),
      onChange: e => {
        onChange(e?.nativeEvent?.text);
      },
      size,
      ...rest
    })
  });
});
const InputSection = styled(XGroup.Item, {
  justifyContent: "center",
  alignItems: "center",
  context: InputContext
});
const Button = styled(TButton, {
  context: InputContext,
  justifyContent: "center",
  alignItems: "center",
  variants: {
    size: {
      "...size": (val = "$true", {
        tokens
      }) => {
        if (typeof val === "number") {
          return {
            paddingHorizontal: 0,
            height: val,
            borderRadius: val * 0.2
          };
        }
        return {
          paddingHorizontal: 0,
          height: val,
          borderRadius: tokens.radius[val]
        };
      }
    }
  }
});
const InputIconFrame = styled(View, {
  justifyContent: "center",
  alignItems: "center",
  context: InputContext,
  variants: {
    size: {
      "...size": (val, {
        tokens
      }) => {
        return {
          paddingHorizontal: tokens.space[val]
        };
      }
    }
  }
});
const getIconSize = (size, scale) => {
  return (typeof size === "number" ? size * 0.5 : getFontSize(size)) * scale;
};
const InputIcon = InputIconFrame.styleable((props, ref) => {
  const {
    children,
    color: colorProp,
    ...rest
  } = props;
  const inputContext = InputContext.useStyledContext();
  const {
    size = "$true",
    color: contextColor,
    scaleIcon = 1
  } = inputContext;
  const theme = useTheme();
  const color = getVariable(contextColor || theme[contextColor]?.get("web") || theme.color10?.get("web"));
  const iconSize = getIconSize(size, scaleIcon);
  const getThemedIcon = useGetThemedIcon({
    size: iconSize,
    color
  });
  return /* @__PURE__ */jsx(InputIconFrame, {
    ref,
    ...rest,
    children: getThemedIcon(children)
  });
});
const InputContainerFrame = styled(View, {
  context: InputContext,
  flexDirection: "column",
  variants: {
    size: {
      "...size": (val, {
        tokens
      }) => ({
        gap: tokens.space[val].val * 0.3
      })
    },
    color: {
      "...color": () => ({})
    },
    gapScale: {
      ":number": {}
    }
  },
  defaultVariants: {
    size: "$4"
  }
});
const InputLabel = styled(Label, {
  context: InputContext,
  variants: {
    size: {
      "...fontSize": getFontSized
    }
  }
});
const InputInfo = styled(Text, {
  context: InputContext,
  color: "$color10",
  variants: {
    size: {
      "...fontSize": (val, {
        font
      }) => {
        if (!font) return;
        const fontSize = font.size[val].val * 0.8;
        const lineHeight = font.lineHeight?.[val].val * 0.8;
        const fontWeight = font.weight?.["$2"];
        const letterSpacing = font.letterSpacing?.[val];
        const textTransform = font.transform?.[val];
        const fontStyle = font.style?.[val];
        return {
          fontSize,
          lineHeight,
          fontWeight,
          letterSpacing,
          textTransform,
          fontStyle
        };
      }
    }
  }
});
const InputXGroup = styled(XGroup, {
  context: InputContext,
  variants: {
    size: {
      "...size": (val, {
        tokens
      }) => {
        const radiusToken = tokens.radius[val] ?? tokens.radius["$true"];
        return {
          borderRadius: radiusToken
        };
      }
    }
  }
});
const Input = withStaticProperties(InputContainerFrame, {
  Box: InputGroupImpl,
  Area: InputImpl,
  Section: InputSection,
  Button,
  Icon: InputIcon,
  Info: InputInfo,
  Label: InputLabel,
  XGroup: withStaticProperties(InputXGroup, {
    Item: XGroup.Item
  })
});
const InputNew = () => {
  return /* @__PURE__ */jsx(Input, {
    w: 400,
    size: "$3",
    children: /* @__PURE__ */jsxs(Input.Box, {
      children: [/* @__PURE__ */jsx(Input.Section, {
        children: /* @__PURE__ */jsx(Input.Icon, {
          children: /* @__PURE__ */jsx(User, {})
        })
      }), /* @__PURE__ */jsx(Input.Section, {
        children: /* @__PURE__ */jsx(Input.Area, {
          paddingLeft: 0
        })
      }), /* @__PURE__ */jsx(Input.Section, {
        children: /* @__PURE__ */jsx(Input.Button, {
          children: /* @__PURE__ */jsx(Input.Icon, {
            children: /* @__PURE__ */jsx(User, {})
          })
        })
      })]
    })
  });
};
export { Input, InputContainerFrame, InputContext, InputIconFrame, InputInfo, InputLabel, InputNew, defaultInputGroupStyles, inputSizeVariant };
//# sourceMappingURL=inputsParts.mjs.map
